import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import {
  MAX_VIDEO_SIZE,
  MAX_RECORDING_SECONDS,
  ACCEPTED_VIDEO_TYPES,
  ACCEPTED_VIDEO_EXTENSIONS,
  getSupportedRecordingMimeType,
} from "@/modules/candidate/constants/candidateApplication.constants";
import type { VideoTabOption } from "@/modules/candidate/types/candidateApplication.types";

export function useVideoCapture() {
  const videoFileInputRef = useRef<HTMLInputElement | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<number | null>(null);

  const [videoTab, setVideoTab] = useState<VideoTabOption>("upload");
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [isVideoDragging, setIsVideoDragging] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const stopRecordingInterval = () => {
    if (recordingIntervalRef.current !== null) {
      window.clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const stopCamera = () => {
    stopRecordingInterval();
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (liveVideoRef.current) {
      liveVideoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    setIsRecording(false);
  };

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      mediaStreamRef.current = stream;

      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        await liveVideoRef.current.play().catch(() => undefined);
      }
      setIsCameraActive(true);
    } catch (err) {
      console.error(err);
      setCameraError("Unable to access your camera and microphone. Please allow permission and try again.");
    }
  };

  const startRecording = () => {
    if (!mediaStreamRef.current) return;

    setVideoError(null);
    recordedChunksRef.current = [];

    const mimeType = getSupportedRecordingMimeType();
    const recorder = mimeType
      ? new MediaRecorder(mediaStreamRef.current, { mimeType })
      : new MediaRecorder(mediaStreamRef.current);

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
      const recordedFile = new File([blob], `candidate-video-${Date.now()}.webm`, { type: "video/webm" });

      setSelectedVideo(recordedFile);
      setVideoPreviewUrl((previousUrl) => {
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        return URL.createObjectURL(blob);
      });

      stopCamera();
    };

    mediaRecorderRef.current = recorder;
    recorder.start(1000);

    setIsRecording(true);
    setRecordingSeconds(0);

    recordingIntervalRef.current = window.setInterval(() => {
      setRecordingSeconds((previousSeconds) => {
        const nextSeconds = previousSeconds + 1;
        if (nextSeconds >= MAX_RECORDING_SECONDS) {
          mediaRecorderRef.current?.stop();
        }
        return nextSeconds;
      });
    }, 1000);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    stopRecordingInterval();
    setIsRecording(false);
  };

  const retakeVideo = () => {
    setVideoPreviewUrl((previousUrl) => {
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      return null;
    });
    setSelectedVideo(null);
    setVideoError(null);
    setRecordingSeconds(0);
    startCamera();
  };

  const formatRecordingTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const validateVideo = async (file: File): Promise<string | null> => {
    const normalizedFileName = file.name.toLowerCase();
    const hasValidExtension = ACCEPTED_VIDEO_EXTENSIONS.some((ext) => normalizedFileName.endsWith(ext));
    const hasValidMimeType = ACCEPTED_VIDEO_TYPES.includes(file.type);

    if (!hasValidExtension && !hasValidMimeType) {
      return "Unsupported video format. Please upload a valid MP4, WEBM, or MOV file.";
    }
    if (file.size > MAX_VIDEO_SIZE) {
      return "The video is too large. Please upload a file smaller than 50 MB.";
    }

    return new Promise<string | null>((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        if (video.duration > MAX_RECORDING_SECONDS) {
          resolve("The video is too long. Please upload a video that is 2 minutes or shorter.");
        } else {
          resolve(null);
        }
      };

      video.onerror = () => {
        resolve("The selected video couldn't be processed. Please choose a valid MP4, WEBM, or MOV file and try again.");
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleVideoSelection = async (file: File) => {
    setVideoError(null);
    const validationError = await validateVideo(file);

    if (validationError) {
      setSelectedVideo(null);
      setVideoError(validationError);
      if (videoFileInputRef.current) videoFileInputRef.current.value = "";
      return;
    }

    setVideoPreviewUrl((previousUrl) => {
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      return URL.createObjectURL(file);
    });
    setSelectedVideo(file);
  };

  const handleVideoFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleVideoSelection(file);
  };

  const handleVideoDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsVideoDragging(true);
  };

  const handleVideoDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsVideoDragging(false);
  };

  const handleVideoDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsVideoDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await handleVideoSelection(file);
  };

  const handleRemoveVideo = () => {
    setVideoPreviewUrl((previousUrl) => {
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      return null;
    });
    setSelectedVideo(null);
    setVideoError(null);
    if (videoFileInputRef.current) videoFileInputRef.current.value = "";
  };

  const handleVideoTabChange = (tab: VideoTabOption) => {
    setVideoTab(tab);
    setVideoError(null);
    if (tab === "upload") stopCamera();
  };

  const reset = () => {
    stopCamera();
    setVideoPreviewUrl((previousUrl) => {
      if (previousUrl) URL.revokeObjectURL(previousUrl);
      return null;
    });
    setSelectedVideo(null);
    setVideoError(null);
    setVideoTab("upload");
    setIsVideoDragging(false);
    setCameraError(null);
    setRecordingSeconds(0);
    if (videoFileInputRef.current) videoFileInputRef.current.value = "";
  };

  useEffect(() => {
    return () => {
      stopCamera();
      setVideoPreviewUrl((previousUrl) => {
        if (previousUrl) URL.revokeObjectURL(previousUrl);
        return previousUrl;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    videoFileInputRef,
    liveVideoRef,
    videoTab,
    selectedVideo,
    videoPreviewUrl,
    isVideoDragging,
    videoError,
    isCameraActive,
    cameraError,
    isRecording,
    recordingSeconds,
    setVideoError,
    startCamera,
    stopCamera,
    startRecording,
    stopRecording,
    retakeVideo,
    formatRecordingTime,
    handleVideoFileChange,
    handleVideoDragOver,
    handleVideoDragLeave,
    handleVideoDrop,
    handleRemoveVideo,
    handleVideoTabChange,
    reset,
  };
}